import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetProjectsQuery, useLogoutMutation } from '../app/api';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { selectProject } from '../features/projects/projectsSlice';
import { useAuth } from '../features/auth/useAuth';
import { RoleBadge, initials } from '../features/admin/adminUi';
import { formatUtcClock } from '../lib/format';

function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

const navLink: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--text-muted)',
  textDecoration: 'none',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '6px 12px',
};

export function TopBar() {
  const now = useClock();
  const dispatch = useAppDispatch();
  const { role } = useAuth();
  const { data: projects } = useGetProjectsQuery();
  const selectedId = useAppSelector((s) => s.projects.selectedId);
  const project = projects?.items.find((p) => p.id === selectedId);
  // Detection is client-driven per rule; the badge reflects how many are ticking.
  const runningCount = useAppSelector(
    (s) => Object.values(s.detection.runtime).filter((r) => r.status === 'running').length,
  );
  const live = runningCount > 0;

  const canManage = role === 'admin' || role === 'super_admin';
  const isSuper = role === 'super_admin';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 22px',
        background: 'var(--surface-raised)',
        borderBottom: '1px solid var(--border)',
        zIndex: 5,
      }}
    >
      {/* brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <img src="/flycommc2-logo.svg" alt="FlycommC2" style={{ height: 46, width: 'auto' }} />
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 18,
              letterSpacing: 0.5,
            }}
          >
            FlycommC2
          </span>
          <span
            className="eyebrow"
            style={{
              fontSize: 11,
              letterSpacing: 1.5,
              color: 'var(--text-dim)',
            }}
          >
            Spectral Awareness
          </span>
        </div>
      </div>

      {/* center: project scope selector + nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="eyebrow" style={{ fontSize: 11, letterSpacing: 1.5, color: 'var(--text-dim)' }}>
          PROJECT
        </span>
        <select
          value={selectedId ?? ''}
          onChange={(e) => dispatch(selectProject(e.target.value || null))}
          style={{
            background: 'var(--surface-app)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            color: project ? 'var(--text-primary)' : 'var(--text-muted)',
            font: 'inherit',
            fontSize: 13,
            padding: '6px 10px',
            minWidth: 180,
          }}
        >
          <option value="">— none (select to scope) —</option>
          {projects?.items.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <Link to="/projects" style={navLink}>
          Projects
        </Link>
        {canManage && (
          <Link to="/users" style={navLink}>
            Users
          </Link>
        )}
        {isSuper && (
          <Link to="/organizations" style={navLink}>
            Organizations
          </Link>
        )}
      </div>

      {/* right: clock + LIVE toggle + user menu */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
        <span
          className="mono"
          style={{ fontSize: 13, color: 'var(--text-secondary)', letterSpacing: 1 }}
        >
          {formatUtcClock(now)} <span style={{ color: 'var(--text-faint)' }}>UTC</span>
        </span>
        <div
          title={live ? `${runningCount} rule${runningCount === 1 ? '' : 's'} running` : 'No rules running'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            background: live ? 'rgba(41,224,168,0.1)' : 'rgba(120,160,200,0.08)',
            border: `1px solid ${live ? 'rgba(41,224,168,0.3)' : 'var(--border)'}`,
            borderRadius: 20,
            padding: '5px 11px',
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: live ? 'var(--ok)' : 'var(--text-dim)',
              boxShadow: live ? '0 0 8px var(--ok)' : 'none',
            }}
          />
          <span
            className="mono"
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 1,
              color: live ? 'var(--ok)' : 'var(--text-muted)',
            }}
          >
            {live ? `${runningCount} RUNNING` : 'IDLE'}
          </span>
        </div>
        <UserMenu />
      </div>
    </div>
  );
}

/** Avatar with a dropdown showing the signed-in user + sign-out. */
function UserMenu() {
  const { user, role } = useAuth();
  const [open, setOpen] = useState(false);
  const [logout] = useLogoutMutation();

  const signOut = async () => {
    try {
      await logout().unwrap();
    } finally {
      // Full reload so all RTK Query / Redux state is cleared.
      window.location.assign('/login');
    }
  };

  if (!user) return null;

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={user.name}
        style={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          background: '#16202c',
          border: open ? '1px solid var(--accent)' : '1px solid rgba(120,160,200,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          color: 'var(--text-secondary)',
          cursor: 'pointer',
        }}
      >
        {initials(user.name)}
      </button>

      {open && (
        <>
          {/* click-away backdrop */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 40 }}
          />
          <div
            style={{
              position: 'absolute',
              top: 44,
              right: 0,
              width: 240,
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hover)',
              borderRadius: 12,
              boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
              padding: 14,
              zIndex: 41,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{user.name}</div>
            <div className="mono" style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
              {user.email}
            </div>
            {role && <RoleBadge role={role} />}
            <button
              type="button"
              onClick={signOut}
              style={{
                width: '100%',
                marginTop: 14,
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 8,
                color: 'var(--text-primary)',
                fontSize: 13,
                fontWeight: 600,
                padding: '9px 12px',
                cursor: 'pointer',
              }}
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
